/**
 * ExperimentDetailSlot
 *
 * Behaviour slot for the `experiments` experience type.
 * Renders an ExperimentDetailPanel inline in chat with
 * fullscreen expansion support.
 *
 * Action buttons (Pause, Stop, etc.) are frontend-only —
 * they call the experiments API directly, not the chat backend.
 */

import React, { useState, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence } from 'motion/react';
import { Maximize2 } from 'lucide-react';
import { IconButton } from '@strivelabs/ui-core/v2';
import { ExperimentDetailPanel } from '@strivelabs/ui-blocks';
import type { ExperimentSlotData } from '@/types/ui-blocks';
import type { BehaviourAgentExperienceAction } from '@/types/ui-blocks';
import { SlotContainer, FullscreenSlotOverlay } from './shared';
import { adaptExperimentChartConfig } from '../experiment-chart-adapter';
import {
  SlotCanvasLayout,
  SlotCanvasPreview,
  useRegisterSlotCanvas,
  useSlotCanvasContext,
} from '@/components/ai-elements/slot-canvas';
import { useSlotCanvasStore } from '@/store/slotCanvasStore';
import { EXPERIENCE_LABELS } from '@/components/ai-elements/slot-navigator/slot-navigator-utils';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface ExperimentDetailSlotProps {
  /** Stable slot id (behaviour.id) used for canvas registration + SlotNavigator */
  slotId?: string;
  action: BehaviourAgentExperienceAction;
  readOnly?: boolean;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const ExperimentDetailSlot: React.FC<ExperimentDetailSlotProps> = ({
  slotId,
  action,
  readOnly,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const slotRef = useRef<HTMLDivElement>(null);
  const canvasCtx = useSlotCanvasContext();
  const isCanvasMode = canvasCtx.mode === 'canvas';
  const canvasBodyMount = useSlotCanvasStore((s) => s.bodyMountElement);
  const resolvedSlotId = slotId ?? action.id;
  const resolvedTitle = action.data?.title ?? EXPERIENCE_LABELS[action.experience];
  const canvasRegistration = useRegisterSlotCanvas({
    id: resolvedSlotId,
    experience: action.experience,
    title: resolvedTitle,
  });

  // Build ExperimentSlotData from the behaviour action's data
  const slotData = useMemo<ExperimentSlotData>(() => {
    const d = (action.data ?? {}) as Record<string, unknown>;
    return {
      title: (d.title as string) ?? '',
      description: (d.description as string) ?? '',
      variant: (d.variant as ExperimentSlotData['variant']) ?? 'analytics',
      experiment_id: d.experiment_id as ExperimentSlotData['experiment_id'],
      channel: d.channel as string | undefined,
      start_date: d.start_date as string | undefined,
      end_date: d.end_date as string | undefined,
      status: d.status as ExperimentSlotData['status'],
      performance_overview: d.performance_overview as ExperimentSlotData['performance_overview'],
      issues: d.issues as ExperimentSlotData['issues'],
      performance_trend: adaptExperimentChartConfig(d.performance_trend),
      variant_comparison: adaptExperimentChartConfig(d.variant_comparison),
      detailed_metrics: d.detailed_metrics as ExperimentSlotData['detailed_metrics'],
      ai_insights: d.ai_insights as ExperimentSlotData['ai_insights'],
      follow_up_experiments: d.follow_up_experiments as ExperimentSlotData['follow_up_experiments'],
      expected_results: d.expected_results as ExperimentSlotData['expected_results'],
      what_it_does: d.what_it_does as string | undefined,
      channel_badge_variant: d.channel_badge_variant as ExperimentSlotData['channel_badge_variant'],
    };
  }, [action.data]);

  // Resolve portal target for fullscreen overlay
  const portalTarget = slotRef.current?.closest('[data-chat-root]') as HTMLElement | null;

  // -- Canvas mode ------------------------------------------------------------
  if (isCanvasMode) {
    return (
      <>
        <SlotCanvasPreview
          id={resolvedSlotId}
          experience={action.experience}
          title={resolvedTitle}
          isActive={canvasRegistration.isOpen}
          onClick={canvasRegistration.open}
        />
        {canvasRegistration.isOpen &&
          canvasBodyMount &&
          createPortal(
            <SlotCanvasLayout
              title={resolvedTitle}
              subtitle={EXPERIENCE_LABELS[action.experience]}
            >
              <ExperimentDetailPanel data={slotData} readOnly={readOnly} />
            </SlotCanvasLayout>,
            canvasBodyMount,
          )}
      </>
    );
  }

  // -- Inline mode (legacy path, unchanged) -----------------------------------
  return (
    <>
      <SlotContainer slotRef={slotRef}>
        {/* Expand button */}
        <IconButton
          variant="secondary"
          size="sm"
          onClick={() => setIsExpanded(true)}
          className="absolute top-3 right-3 z-10"
          aria-label="Expand"
        >
          <Maximize2 className="w-4 h-4" />
        </IconButton>

        <ExperimentDetailPanel
          data={slotData}
          compact
          readOnly={readOnly}
        />
      </SlotContainer>

      {/* Fullscreen overlay */}
      <AnimatePresence>
        {isExpanded && portalTarget && (
          <FullscreenSlotOverlay
            onClose={() => setIsExpanded(false)}
            portalTarget={portalTarget}
          >
            <ExperimentDetailPanel
              data={slotData}
              readOnly={readOnly}
            />
          </FullscreenSlotOverlay>
        )}
      </AnimatePresence>
    </>
  );
};
